import Avatar, { AvatarSize } from '@app/src/components/Ui/Avatar'
import { palette } from '@app/src/theme/palette'
import type { SvgIconComponent } from '@mui/icons-material'
import { Box, Card, CardActionArea, Stack, Tooltip, Typography } from '@mui/material'
import React from 'react'

interface AutomationTypeCardProps {
  onClick: () => void
  icon: SvgIconComponent
  title: string
  description: string
  variant?: 'default' | 'ai' | 'expectation'
  disabled?: boolean
  disabledTooltip?: string
}

const AutomationTypeCard: React.FC<AutomationTypeCardProps> = ({
  onClick,
  icon,
  title,
  description,
  variant = 'default',
  disabled,
  disabledTooltip,
}) => {
  const colorSchemes = {
    default: { icon: 'secondary.light', bg: 'success.light' },
    ai: { icon: 'communication.dark', bg: 'communication.light' },
    expectation: { icon: palette.visualization[1], bg: 'info.light' },
  } as const

  const { icon: iconColor, bg: bgColor } = colorSchemes[variant]

  const card = (
    <Card
      elevation={0}
      variant="outlined"
      sx={{
        height: '100%',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      <CardActionArea
        onClick={onClick}
        disabled={disabled}
        sx={{
          height: '100%',
          padding: 3,
        }}
      >
        <Stack spacing={2} height="100%">
          <Avatar size={AvatarSize.XL} variant="circular" sx={{ bgcolor: bgColor, color: iconColor }}>
            {React.createElement(icon)}
          </Avatar>
          <Typography variant="subtitle1" color={disabled ? 'textSecondary' : 'textPrimary'}>
            {title}
          </Typography>
          <Stack flexGrow={1} height="100%">
            <Typography color="textSecondary">{description}</Typography>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  )

  if (disabled && disabledTooltip) {
    return (
      <Tooltip title={disabledTooltip} arrow>
        <Box sx={{ height: '100%' }}>{card}</Box>
      </Tooltip>
    )
  }

  return card
}

export default AutomationTypeCard
